/* oxlint-disable anti-slop/no-runtime-typeof */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faEye, faRocket, faStop, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import useUser from '../hooks/useUser';
import { listBrokerCredentials, listBrokerSnapshots, listEnvVarSets } from '../utils/savedSecrets';
import { getBrokerAccountLabel, getBrokerConnectionTitle } from '../utils/brokerConnectionDisplay';
import { brokerConnectionNeedsAttention } from '../utils/brokerConnectionStatus';
import {
  getReservedBrokerEnvVarNames,
  hasReservedBrokerEnvVarName,
} from '../utils/reservedEnvVarNames';
import getApiBaseUrl from '../utils/apiBaseUrl';
import { formatIntervalCadence, formatScheduleCadence } from '../utils/scheduleCadence';
import ActionAuthorizationCard from './AiAgent/components/shared/ActionAuthorizationCard';
import './ActionAuthorizationPage.css';

const API_URL = getApiBaseUrl();
const STOP_BOT_POSITION_ACKNOWLEDGEMENT =
  'I understand stopping this bot only stops future automated trading changes. It will not sell, close, or liquidate any positions already in my brokerage account, and I am responsible for reviewing open positions and orders with my broker.';

function actionLabel(actionType) {
  if (actionType === 'start_deployment') return 'Start Bot';
  if (actionType === 'stop_deployment') return 'Stop Bot';
  if (actionType === 'update_deployment_revision') return 'Update Bot';
  if (actionType === 'place_trade') return 'Place Trade';
  return actionType || 'Deployment Action';
}

function actionTitle(actionType) {
  if (actionType === 'start_deployment') return 'Start Bot';
  if (actionType === 'stop_deployment') return 'Stop Bot';
  if (actionType === 'update_deployment_revision') return 'Update Bot';
  if (actionType === 'place_trade') return 'Place This Trade';
  return actionLabel(actionType);
}

function actionCtaIcon(actionType) {
  if (actionType === 'place_trade') return faChartLine;
  if (actionType === 'stop_deployment') return faStop;
  if (actionType === 'update_deployment_revision') return faSyncAlt;
  if (actionType === 'start_deployment') return faRocket;
  return faRocket;
}

function isApprovedActionStatus(status) {
  return status === 'completed' || status === 'executing';
}

function isTerminalActionStatus(status) {
  return (
    isApprovedActionStatus(status) ||
    status === 'failed' ||
    status === 'denied' ||
    status === 'expired'
  );
}

function completedActionTitle(action) {
  if (!action) return 'Authorization Updated';
  if (isApprovedActionStatus(action.status)) {
    if (action.actionType === 'place_trade') return 'Trade Approved';
    if (action.actionType === 'stop_deployment') return 'Bot Stop Approved';
    if (action.actionType === 'update_deployment_revision') return 'Bot Update Approved';
    return 'Bot Start Approved';
  }
  if (action.status === 'denied') return 'Request Denied';
  if (action.status === 'expired') return 'Approval Link Expired';
  if (action.status === 'failed') return 'Action Failed';
  return actionTitle(action.actionType);
}

function confirmLoginLabel(actionType) {
  if (actionType === 'stop_deployment') return 'Log in to stop';
  if (actionType === 'update_deployment_revision') return 'Log in to update';
  if (actionType === 'place_trade') return 'Log in to trade';
  return 'Log in to start';
}

function isStartLikeAction(actionType) {
  return actionType === 'start_deployment' || actionType === 'update_deployment_revision';
}

function formatDate(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatScheduleDate(value) {
  const date = parseDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

function formatClientLabel(clientId) {
  if (!clientId) return 'BotSpot MCP client';
  if (clientId.startsWith('mcp-token:')) return 'BotSpot MCP client';
  return clientId;
}

function formatBroker(value) {
  if (!value) return 'Not specified';
  return String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => {
      const normalized = part.toLowerCase();
      if (normalized === 'multileg') return 'Multi Leg';
      return part.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(' ');
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function displayValue(value, fallback = 'Not specified') {
  return hasValue(value) ? String(value) : fallback;
}

function formatLegCount(count, fallback = 'Multi') {
  const numericCount = Number(count);
  if (Number.isFinite(numericCount) && numericCount > 0) {
    return `${numericCount} ${numericCount === 1 ? 'leg' : 'legs'}`;
  }
  return `${fallback} legs`;
}

function formatSideClass(value) {
  return (
    String(value || 'unknown')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'unknown'
  );
}

function formatBrokerConnection(item) {
  if (!item) return 'Choose a broker connection';
  const broker = formatBroker(item.broker);
  const mode = item.paperOrLive === 'paper' ? 'paper' : item.paperOrLive === 'live' ? 'live' : null;
  const title = getBrokerConnectionTitle(item) || item.displayName;
  const account = getBrokerAccountLabel(item);
  const hasLatestAccountValue =
    item.latestAccountValue !== null &&
    item.latestAccountValue !== undefined &&
    item.latestAccountValue !== '';
  const latestAccountValue = hasLatestAccountValue ? Number(item.latestAccountValue) : null;
  const accountValue = Number.isFinite(latestAccountValue)
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }).format(latestAccountValue)
    : null;
  return [broker, account, mode, accountValue, title].filter(Boolean).join(' · ');
}

function getAuthoritativeBrokerAccountId(item) {
  return String(item?.externalAccountId || item?.externalAccountLabel || '').trim();
}

function formatAccountValue(value) {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 'Unavailable';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(numericValue);
}

function getSnapshotAccountValue(snapshot) {
  const candidates = [
    snapshot?.account?.latestPortfolioValue,
    snapshot?.account?.portfolioValue,
    snapshot?.account?.portfolio_value,
    snapshot?.latestPortfolioValue,
    snapshot?.portfolioValue,
    snapshot?.portfolio_value,
    snapshot?.latest_portfolio_value,
  ];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue;
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function uniqueDisplayParts(values) {
  const seen = new Set();
  return values
    .filter(hasValue)
    .map((value) => String(value).trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function formatBrokerAccountLabel(connection) {
  const parts = uniqueDisplayParts([
    getBrokerConnectionTitle(connection) || connection?.displayName,
    getBrokerAccountLabel(connection),
  ]);
  return parts.length ? parts.join(' · ') : 'Saved broker connection';
}

function brokerConnectionSortKey(item) {
  return [
    formatBroker(item?.broker),
    item?.displayName || '',
    item?.paperOrLive || '',
    item?.externalAccountLabel || item?.externalAccountId || '',
  ]
    .join('\u0000')
    .toLowerCase();
}

function brokerNeedsReconnect(item) {
  return brokerConnectionNeedsAttention(item);
}

function formatEnvVarSet(item) {
  if (!item) return 'Unknown environment variable set';
  return item.displayName || 'Unknown environment variable set';
}

function formatEnvVarSearchText(item) {
  if (!item) return '';
  return [item.displayName, ...(Array.isArray(item.keyNames) ? item.keyNames : [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function formatEnvVarKeys(item) {
  if (!Array.isArray(item?.keyNames) || !item.keyNames.length) return '';
  return item.keyNames.join(', ');
}

function formatEnvVarSetSummary(item, requiredMatchCount) {
  if (requiredMatchCount > 0) return '';
  const count = Array.isArray(item?.keyNames) ? item.keyNames.length : 0;
  if (!count) return 'No saved variables listed';
  const label = count === 1 ? 'saved variable' : 'saved variables';
  return `Includes ${count} ${label}: ${formatEnvVarKeys(item)}`;
}

function readNestedValue(source, path) {
  if (!source || typeof source !== 'object') return undefined;
  return path.reduce(
    (value, key) => (value && typeof value === 'object' ? value[key] : undefined),
    source,
  );
}

function cadenceFromObject(value) {
  return formatScheduleCadence(value);
}

function getActionScheduleCadence(action, preview) {
  const payload = action?.payloadRedacted || {};
  const metadata = action?.metadata || {};
  const candidates = [
    preview,
    preview?.schedule,
    preview?.policy,
    preview?.policyJson,
    metadata.schedule,
    metadata.schedulePolicy,
    metadata.schedulePolicy?.policyJson,
    metadata.revisionScheduleMetadata,
    payload.schedule,
    payload.schedulePolicy,
    payload.schedulePolicy?.policyJson,
    payload.revisionScheduleMetadata,
  ];

  for (const candidate of candidates) {
    const formatted = cadenceFromObject(candidate);
    if (formatted) return formatted;
  }

  const nestedInterval = [
    ['schedulePreview', 'cadence', 'intervalSeconds'],
    ['schedulePreview', 'policyJson', 'cadence', 'intervalSeconds'],
    ['policyJson', 'cadence', 'intervalSeconds'],
    ['metadata', 'intervalSeconds'],
  ].map((path) =>
    readNestedValue(
      { metadata, payload, schedulePreview: preview, policyJson: metadata.policyJson },
      path,
    ),
  );

  for (const value of nestedInterval) {
    const formatted = formatIntervalCadence(value);
    if (formatted) return formatted;
  }

  const nextRuns = Array.isArray(preview?.nextRuns)
    ? preview.nextRuns.filter((item) => item && typeof item === 'object')
    : [];
  const nextRunLabel = nextRuns[0]?.label;
  if (
    nextRunLabel &&
    !nextRunLabel.match(/\b\d{1,2}:\d{2}\b|UTC|AM|PM/i) &&
    !nextRunLabel.match(/^(scheduled|weekly scheduled|monthly scheduled|interval scheduled) run$/i)
  ) {
    return String(nextRunLabel);
  }

  return preview ? 'Custom Schedule' : null;
}

function getActionSchedulePreview(action) {
  const preview =
    action?.metadata?.schedulePreview || action?.payloadRedacted?.schedulePreview || null;
  if (!preview || typeof preview !== 'object') return null;
  const nextRuns = Array.isArray(preview?.nextRuns)
    ? preview.nextRuns.filter((item) => item && typeof item === 'object')
    : [];
  const resolvedMode =
    preview?.resolvedMode ||
    action?.metadata?.schedulePolicy?.resolvedDeploymentMode ||
    action?.metadata?.resolvedDeploymentMode ||
    action?.payloadRedacted?.schedulePolicy?.resolvedDeploymentMode ||
    null;
  const nextRun = resolveNextScheduleRun(preview, nextRuns);
  return {
    nextRun,
    requestedMode: preview?.requestedMode || null,
    resolvedMode,
    reason: preview?.reason || null,
    intervalSeconds: preview?.intervalSeconds ?? null,
    thresholdSeconds: preview?.thresholdSeconds ?? null,
    allowlisted: preview?.allowlisted ?? null,
    cadence: getActionScheduleCadence(action, preview),
  };
}

function getRunTargetUtc(run) {
  return run?.targetUtc || run?.targetAt || run?.runAt || run?.target || null;
}

function getPreviewIntervalSeconds(preview) {
  const value = preview?.intervalSeconds ?? preview?.schedule?.cadence?.intervalSeconds;
  const intervalSeconds = Number(value);
  return Number.isFinite(intervalSeconds) && intervalSeconds > 0 ? intervalSeconds : null;
}

function formatScheduleCadenceLabel(preview) {
  if (preview?.resolvedMode !== 'scheduled') return '';
  return formatIntervalCadence(getPreviewIntervalSeconds(preview)) || preview?.cadence || '';
}

function resolveNextScheduleRun(preview, nextRuns, now = new Date()) {
  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) return nextRuns[0] || null;
  const runsWithDates = nextRuns
    .map((run) => ({ run, targetDate: parseDate(getRunTargetUtc(run)) }))
    .filter((item) => item.targetDate);
  const nextFutureRun = runsWithDates.find((item) => item.targetDate.getTime() > nowMs);
  if (nextFutureRun) {
    return {
      ...nextFutureRun.run,
      targetLocal: formatScheduleDate(nextFutureRun.targetDate),
    };
  }

  const intervalSeconds = getPreviewIntervalSeconds(preview);
  const anchor = runsWithDates[0]?.targetDate;
  if (!intervalSeconds || !anchor) return runsWithDates.length ? null : nextRuns[0] || null;

  const intervalMs = intervalSeconds * 1000;
  const elapsedMs = nowMs - anchor.getTime();
  const tickCount = elapsedMs >= 0 ? Math.floor(elapsedMs / intervalMs) + 1 : 0;
  const computedTarget = new Date(anchor.getTime() + tickCount * intervalMs);
  return {
    ...runsWithDates[0].run,
    targetUtc: computedTarget.toISOString(),
    targetLocal: formatScheduleDate(computedTarget),
  };
}

function formatScheduleRouteLabel(preview) {
  if (preview?.resolvedMode === 'scheduled') return 'Scheduled Deployment';
  if (preview?.resolvedMode === 'always_on') return 'Always On';
  return '';
}

function currentBrokerFromAction(action) {
  const metadata = action?.metadata || {};
  const current = metadata.currentBroker;
  if (!current || typeof current !== 'object') return null;
  return current;
}

function selectedBrokerIdFromAction(action) {
  const payload = action?.payloadRedacted || {};
  const metadata = action?.metadata || {};
  const currentBroker = currentBrokerFromAction(action);
  const candidates = [
    payload.brokerCredentialId,
    metadata.brokerCredentialId,
    metadata.brokerCredential?.id,
    // Update/restart: surface Deploy.brokerCredentialId (same source as Bots UI)
    // for preselect/display only. Node never copies this into the escrow payload.
    action?.actionType === 'update_deployment_revision' ? currentBroker?.brokerCredentialId : null,
  ];
  return candidates.find((value) => typeof value === 'string' && value.trim()) || '';
}

function selectedEnvIdsFromAction(action) {
  const payload = action?.payloadRedacted || {};
  const metadata = action?.metadata || {};
  const payloadIds = Array.isArray(payload.envVarSetIds) ? payload.envVarSetIds : [];
  const metadataIds = Array.isArray(metadata.envVarSets)
    ? metadata.envVarSets.map((item) => item?.id)
    : [];
  return Array.from(
    new Set(
      [...payloadIds, ...metadataIds].filter((value) => typeof value === 'string' && value.trim()),
    ),
  );
}

function actionPaperOrLive(action) {
  const payload = action?.payloadRedacted || {};
  const metadata = action?.metadata || {};
  const trade = payload.trade && typeof payload.trade === 'object' ? payload.trade : {};
  const candidates = [
    metadata.paperOrLive,
    payload.paperOrLive,
    trade.paperOrLive,
    metadata.brokerCredential?.paperOrLive,
    metadata.brokerCredentialMetadata?.paperOrLive,
    payload.brokerCredential?.paperOrLive,
  ];
  const value = candidates.find((item) => typeof item === 'string' && item.trim());
  return value ? value.trim().toLowerCase() : '';
}

function liveRiskAcknowledgementText(actionType) {
  if (actionType === 'place_trade') {
    return 'I understand this places a live trade with real money.';
  }
  if (actionType === 'update_deployment_revision') {
    return 'I understand this updates live trading with real money.';
  }
  return 'I understand this starts live trading with real money.';
}

function normalizeEnvKey(value) {
  return typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : '';
}

function normalizeRequirementItem(entry) {
  if (typeof entry === 'string') {
    const key = normalizeEnvKey(entry);
    return key ? { key } : null;
  }
  if (!entry || typeof entry !== 'object') return null;
  const key = normalizeEnvKey(entry.key || entry.name || entry.envVar || entry.envVarName);
  if (!key) return null;
  return {
    key,
    title:
      typeof entry.title === 'string'
        ? entry.title
        : typeof entry.label === 'string'
          ? entry.label
          : null,
    description: typeof entry.description === 'string' ? entry.description : null,
  };
}

function normalizeRequirementItems(value) {
  const rawItems = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set();
  const items = [];
  rawItems.forEach((entry) => {
    const item = normalizeRequirementItem(entry);
    if (!item || seen.has(item.key)) return;
    seen.add(item.key);
    items.push(item);
  });
  return items;
}

function requirementAppliesToAction(requirement, actionType) {
  const context = actionType === 'update_deployment_revision' ? 'update' : 'deploy';
  if (!Array.isArray(requirement.requiredFor) || requirement.requiredFor.length === 0) return true;
  return requirement.requiredFor.includes(context);
}

function normalizeRuntimeRequirements(action) {
  // The server owns managed-provider satisfaction; analysis metadata is not an approval gate.
  // Compatibility for released APIs without the projection. Remove once the minimum
  // API contract requires it. This adapter must never invent managed satisfaction.
  const doc =
    action?.runtimeSecretReadiness?.requirements ?? action?.metadata?.runtimeSecretRequirements;
  const rawRequirements = Array.isArray(doc?.requirements) ? doc.requirements : [];
  return rawRequirements
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const allOf = normalizeRequirementItems(entry.allOf || entry.keys);
      const anyOf = normalizeRequirementItems(entry.anyOf);
      if (!allOf.length && !anyOf.length) return null;
      return {
        id: entry.id || null,
        label:
          typeof entry.label === 'string'
            ? entry.label
            : typeof entry.title === 'string'
              ? entry.title
              : null,
        description: typeof entry.description === 'string' ? entry.description : null,
        requiredFor: Array.isArray(entry.requiredFor) ? entry.requiredFor : [],
        allOf,
        anyOf,
      };
    })
    .filter(Boolean)
    .filter((entry) => requirementAppliesToAction(entry, action?.actionType));
}

function collectSelectedEnvKeys(items) {
  const keys = new Set();
  items.forEach((item) => {
    if (!Array.isArray(item?.keyNames)) return;
    item.keyNames.forEach((key) => {
      const normalized = normalizeEnvKey(key);
      if (normalized) keys.add(normalized);
    });
  });
  return keys;
}

function runtimeRequirementSatisfied(requirement, selectedKeys) {
  const allOfSatisfied = requirement.allOf.every((item) => selectedKeys.has(item.key));
  const anyOfSatisfied =
    requirement.anyOf.length === 0 || requirement.anyOf.some((item) => selectedKeys.has(item.key));
  return allOfSatisfied && anyOfSatisfied;
}

function requirementDisplayKeys(requirement) {
  const allOf = requirement.allOf.map((item) => item.key);
  const anyOf = requirement.anyOf.map((item) => item.key);
  if (allOf.length && anyOf.length) return `${allOf.join(', ')} and one of ${anyOf.join(', ')}`;
  if (allOf.length) return allOf.join(', ');
  return `one of ${anyOf.join(', ')}`;
}

function envVarRequiredKeyMatchCount(item, requiredKeys) {
  if (!requiredKeys.size || !Array.isArray(item?.keyNames)) return 0;
  return item.keyNames.reduce(
    (count, key) => (requiredKeys.has(normalizeEnvKey(key)) ? count + 1 : count),
    0,
  );
}

function priceRow(label, value) {
  return hasValue(value) ? { label, value } : null;
}

const TRADE_PRICE_DETAIL_ROWS = [
  ['Limit', 'limitPrice'],
  ['Stop', 'stopPrice'],
  ['Stop limit', 'stopLimitPrice'],
  ['Trail price', 'trailPrice'],
  ['Trail percent', 'trailPercent'],
  ['Take profit', 'secondaryLimitPrice'],
  ['Stop loss', 'secondaryStopPrice'],
  ['Stop loss limit', 'secondaryStopLimitPrice'],
];

function formatOptionContract(trade) {
  if (!trade || String(trade.assetType || '').toLowerCase() !== 'option') return null;
  return [trade.expiration, trade.right ? String(trade.right).toUpperCase() : null, trade.strike]
    .filter(hasValue)
    .join(' ');
}

function formatLegSummary(leg, index) {
  const side = formatBroker(leg.side || `Leg ${index + 1}`);
  const quantity = displayValue(leg.quantity);
  const symbol = displayValue(leg.symbol, 'Symbol');
  const option = formatOptionContract(leg);
  const orderType = leg.orderType ? `${formatBroker(leg.orderType)} order` : null;
  const parts = [`${side} ${quantity} ${symbol}`, option, orderType].filter(hasValue);
  return parts.join(' · ');
}

function formatExitSummary(label, exit) {
  if (!exit || typeof exit !== 'object') return null;
  const orderType = formatBroker(exit.orderType || (label === 'Take profit' ? 'limit' : 'stop'));
  const priceParts = [
    priceRow('Limit', exit.limitPrice),
    priceRow('Stop', exit.stopPrice),
    priceRow('Stop limit', exit.stopLimitPrice),
    priceRow('Trail price', exit.trailPrice),
    priceRow('Trail percent', exit.trailPercent),
  ].filter(Boolean);
  const prices = priceParts.map((item) => `${item.label} ${item.value}`).join(' · ');
  const exitDetails = [
    hasValue(exit.quantity) ? `Qty ${displayValue(exit.quantity)}` : null,
    exit.timeInForce ? `TIF ${String(exit.timeInForce).toUpperCase()}` : null,
  ]
    .filter(hasValue)
    .join(' · ');
  return {
    label,
    value: [orderType, exitDetails, prices].filter(hasValue).join(' · '),
  };
}

function getDeploymentDetails(action, selectedBroker = null) {
  if (!action) return {};

  const payload = action?.payloadRedacted || {};
  const metadata = action?.metadata || {};
  const botConfig = payload.bot_config || payload.botConfig || {};
  const termsAccepted = Boolean(
    payload.acceptances?.acceptedTerms && payload.acceptances?.acceptedBetaRisk,
  );
  const strategyName =
    metadata.strategyName ||
    payload.strategyName ||
    botConfig.strategyName ||
    metadata.botName ||
    payload.botName ||
    null;
  const currentBroker = currentBrokerFromAction(action);
  const brokerCredential =
    selectedBroker ||
    metadata.brokerCredential ||
    metadata.brokerCredentialMetadata ||
    payload.brokerCredential ||
    currentBroker ||
    null;
  const brokerCredentialName =
    metadata.brokerDisplayName ||
    payload.brokerDisplayName ||
    metadata.brokerCredentialDisplayName ||
    brokerCredential?.displayName ||
    brokerCredential?.brokerCredentialDisplayName ||
    brokerCredential?.externalAccountLabel ||
    brokerCredential?.externalAccountId ||
    currentBroker?.brokerCredentialDisplayName ||
    null;
  const brokerCredentialDisplayName =
    metadata.brokerCredentialDisplayName ||
    brokerCredential?.displayName ||
    brokerCredential?.brokerCredentialDisplayName ||
    currentBroker?.brokerCredentialDisplayName ||
    null;
  const externalAccountLabel =
    metadata.externalAccountLabel || brokerCredential?.externalAccountLabel || null;
  const externalAccountId =
    metadata.externalAccountId || brokerCredential?.externalAccountId || null;
  const broker =
    botConfig.broker ||
    metadata.broker ||
    payload.broker ||
    brokerCredential?.broker ||
    currentBroker?.broker;
  const hasSavedBrokerCredential = Boolean(
    payload.brokerCredentialId ||
    metadata.brokerCredentialId ||
    currentBroker?.brokerCredentialId ||
    brokerCredentialName,
  );

  if (action?.actionType === 'place_trade') {
    const trade = payload.trade || {};
    const read = (key, fallback) => metadata[key] ?? trade[key] ?? payload[key] ?? fallback;
    const bracketEntry = trade.entry && typeof trade.entry === 'object' ? trade.entry : null;
    const legs = Array.isArray(bracketEntry?.legs)
      ? bracketEntry.legs
      : Array.isArray(trade.legs)
        ? trade.legs
        : [];
    const firstLeg = legs[0] || {};
    const isBracket = read('orderClass', '') === 'multileg_bracket' || Boolean(bracketEntry);
    const isMultiLeg = legs.length > 0 || read('orderClass', '') === 'multileg' || isBracket;
    const side = isMultiLeg ? 'multi leg' : read('side', 'Not specified');
    const orderType = isBracket ? bracketEntry?.orderType || 'market' : read('orderType', 'market');
    const timeInForce = isBracket
      ? bracketEntry?.timeInForce || read('timeInForce', 'day')
      : read('timeInForce', 'day');
    const orderClass = read(
      'orderClass',
      isBracket ? 'multileg_bracket' : isMultiLeg ? 'multileg' : 'simple',
    );
    const assetType = read('assetType', firstLeg.assetType || 'stock');
    // A broker-neutral approval has no mode until the user selects a saved
    // connection. Never imply real-money trading merely because mode is absent.
    const paperOrLive = read('paperOrLive', brokerCredential?.paperOrLive || null);
    const accountLabel =
      metadata.accountLabel ||
      payload.accountLabel ||
      formatBrokerAccountLabel({
        displayName: brokerCredentialDisplayName,
        externalAccountLabel,
        externalAccountId,
      });
    const analysisEvidence = Array.isArray(metadata.analysisEvidence)
      ? metadata.analysisEvidence
      : Array.isArray(payload.analysisEvidence)
        ? payload.analysisEvidence
        : [];
    const optionContract = formatOptionContract(trade) || formatOptionContract(firstLeg);
    const exitRows = [
      formatExitSummary('Take profit', trade.takeProfit),
      formatExitSummary('Stop loss', trade.stopLoss),
    ].filter(Boolean);
    const detailRows = [
      {
        label: 'Broker',
        value: formatBroker(metadata.broker || payload.broker || brokerCredential?.broker),
      },
      { label: 'Account', value: accountLabel },
      { label: 'Mode', value: formatBroker(paperOrLive) },
      { label: 'Asset', value: formatBroker(assetType) },
      orderClass !== 'simple' && { label: 'Class', value: formatBroker(orderClass) },
      optionContract && { label: 'Contract', value: optionContract },
      ...TRADE_PRICE_DETAIL_ROWS.map(([label, key]) =>
        priceRow(label, isBracket ? bracketEntry?.[key] : read(key, null)),
      ),
    ].filter(Boolean);

    return {
      action: 'Place Trade',
      strategyName: strategyName || 'Selected strategy',
      symbol: isBracket
        ? bracketEntry?.symbol || firstLeg.symbol || 'Selected symbol'
        : read('symbol', firstLeg.symbol || 'Selected symbol'),
      quantity: isMultiLeg
        ? formatLegCount(legs.length, read('quantity', 'Multi'))
        : read('quantity', 'Not specified'),
      side: formatBroker(side),
      sideClass: formatSideClass(side),
      orderLabel: `${formatBroker(orderType)} order · ${String(timeInForce || 'day').toUpperCase()}`,
      detailRows: [
        { label: 'Broker connection', value: brokerCredentialName || detailRows[0]?.value },
        { label: 'Account', value: accountLabel },
        ...detailRows.filter((row) => !['Broker', 'Account'].includes(row.label)),
      ],
      analysisEvidence: analysisEvidence.map((evidence) => ({
        target: evidence.target || 'price',
        resolutionId: evidence.resolutionId || null,
        label: evidence.label || evidence.target || 'Resolved analysis value',
        value: displayValue(evidence.value, 'Not available'),
        dataTimestamp: formatDate(evidence.asOf || evidence.resolvedAt),
      })),
      entryLabel: isBracket ? 'Entry legs' : 'Legs',
      legs: legs.map(formatLegSummary),
      exitRows,
      requestedBy: formatClientLabel(action.clientId),
      expiresAt: formatDate(action.expiresAt),
    };
  }

  if (action?.actionType === 'stop_deployment') {
    return {
      action: 'Stop Bot',
      strategyName: strategyName || metadata.deploymentName || 'Selected live bot',
      requestedBy: formatClientLabel(action.clientId),
      expiresAt: formatDate(action.expiresAt),
    };
  }

  if (action?.actionType === 'update_deployment_revision') {
    const currentVersion =
      metadata.currentRevisionVersion ?? payload.currentRevisionVersion ?? null;
    const targetVersion = metadata.targetRevisionVersion ?? payload.targetRevisionVersion ?? null;
    const versionChanged =
      currentVersion != null &&
      targetVersion != null &&
      String(currentVersion) !== String(targetVersion);
    return {
      action: 'Update Bot',
      strategyName: strategyName || metadata.deploymentName || 'Selected live bot',
      currentVersion: currentVersion ? `Version ${currentVersion}` : 'Current running version',
      targetVersion: targetVersion ? `Version ${targetVersion}` : 'Target version',
      versionChanged,
      broker: formatBroker(broker),
      credentialSource: hasSavedBrokerCredential
        ? brokerCredentialName || 'Saved broker connection'
        : 'Choose a saved connection',
      riskTerms: termsAccepted ? 'Accepted' : 'Not included',
      requestedBy: formatClientLabel(action.clientId),
      expiresAt: formatDate(action.expiresAt),
    };
  }

  return {
    action: 'Start Bot',
    strategyName: strategyName || metadata.deploymentName || 'Selected strategy',
    broker: formatBroker(broker),
    credentialSource: hasSavedBrokerCredential
      ? brokerCredentialName || 'Saved broker connection'
      : 'Choose a saved connection',
    riskTerms: termsAccepted ? 'Accepted' : 'Not included',
    requestedBy: formatClientLabel(action.clientId),
    expiresAt: formatDate(action.expiresAt),
  };
}

function statusCopy(action) {
  if (!action) return '';
  if (isApprovedActionStatus(action.status)) {
    if (action.actionType === 'place_trade') {
      const executionStatus = action.result?.status || action.resultStatus;
      if (executionStatus === 'queued') return 'Trade approved and queued.';
      if (executionStatus === 'filled') return 'Trade approved and filled.';
      return 'Trade approved and submitted.';
    }
    if (action.actionType === 'stop_deployment') {
      return 'This bot was stopped.';
    }
    if (action.actionType === 'update_deployment_revision') {
      return 'This bot was updated. The old version was stopped and the new version was started.';
    }
    return 'This bot was started.';
  }
  if (action.status === 'denied') return 'Nothing will run.';
  if (action.status === 'expired')
    return 'This approval request expired. Ask the AI agent to prepare a new one.';
  if (action.status === 'failed') return 'This action failed. No further approval is needed.';
  return '';
}

function actionStatusTone(action) {
  if (!action) return '';
  if (isApprovedActionStatus(action.status)) return 'success';
  if (action.status === 'failed' || action.status === 'denied' || action.status === 'expired') {
    return 'failure';
  }
  return '';
}

function isInternalDecisionMessage(message) {
  const text = String(message || '')
    .trim()
    .toLowerCase();
  if (!text) return true;
  if (/action is \w+ and cannot be approved/.test(text)) return true;
  if (text.includes('action approved and execution started')) return true;
  if (text.includes('save money') || text.includes('saves money')) return true;
  if (text.includes('botspot auto selected')) return true;
  if (text.includes('scheduled bot')) return true;
  if (text.includes('accept this mismatch')) return true;
  if (text.includes('live cadence differs from the backtest')) return true;
  if (text.includes('backtest iterated') && text.includes('live deployment')) return true;
  return false;
}

function isLegacyHistoryAccountCorrectionError(data) {
  return data?.errorCode === 'LEGACY_HISTORY_ACCOUNT_CORRECTION_REQUIRED';
}

function customerFacingDecisionError(message, nextAction) {
  if (nextAction && isTerminalActionStatus(nextAction.status)) return '';
  if (isInternalDecisionMessage(message)) {
    return 'This action failed. No further approval is needed.';
  }
  return message;
}

function normalizeTradeDraftForEditing(trade) {
  const next = { ...(trade || {}) };
  [
    'quantity',
    'limitPrice',
    'stopPrice',
    'stopLimitPrice',
    'secondaryLimitPrice',
    'secondaryStopPrice',
    'secondaryStopLimitPrice',
  ].forEach((field) => {
    if (next[field] === undefined || next[field] === null || next[field] === '') return;
    const numeric = Number(next[field]);
    if (Number.isFinite(numeric)) next[field] = numeric;
  });
  return next;
}

export default function ActionAuthorizationPage(props = {}) {
  const actionIdOverride = props?.actionIdOverride;
  const { actionId: routeActionId } = useParams();
  const actionId = actionIdOverride || routeActionId;
  const navigate = useNavigate();
  const location = useLocation();
  const { getApiToken, getFreshLoginIdToken, isAuthenticated, isLoading } = useUser();
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [envVarOptions, setEnvVarOptions] = useState([]);
  const [selectedBrokerCredentialId, setSelectedBrokerCredentialId] = useState('');
  const [tradeDraft, setTradeDraft] = useState({});
  const [selectedEnvVarSetIds, setSelectedEnvVarSetIds] = useState([]);
  const [envVarsExpanded, setEnvVarsExpanded] = useState(false);
  const [envVarSearch, setEnvVarSearch] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedLiveRisk, setAcceptedLiveRisk] = useState(false);
  const [acceptedStopNonLiquidation, setAcceptedStopNonLiquidation] = useState(false);
  const [acceptedLegacyHistoryAccountCorrection, setAcceptedLegacyHistoryAccountCorrection] =
    useState(false);
  const [legacyHistoryAccountCorrection, setLegacyHistoryAccountCorrection] = useState(null);
  const pageClassName = props?.embedded
    ? 'action-auth-page action-auth-page--embedded'
    : 'action-auth-page';

  const loadAction = useCallback(async () => {
    if (!API_URL) {
      setError('Missing API configuration.');
      setLoading(false);
      return;
    }
    if (!isAuthenticated) {
      setAction(null);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) {
        throw new Error('Authentication token was unavailable.');
      }
      const freshLoginIdToken =
        typeof getFreshLoginIdToken === 'function' ? await getFreshLoginIdToken() : null;
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      if (freshLoginIdToken) {
        headers['X-BotSpot-Fresh-Login-Id-Token'] = freshLoginIdToken;
      }
      const response = await fetch(`${API_URL}/action-authorizations/${actionId}`, {
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load authorization request.');
      }
      const nextAction = data.action;
      setAction(nextAction);
      if (nextAction?.actionType === 'place_trade') {
        setTradeDraft(normalizeTradeDraftForEditing(nextAction?.payloadRedacted?.trade));
      }
      const shouldLoadBrokerOptions =
        isStartLikeAction(nextAction?.actionType) || nextAction?.actionType === 'place_trade';
      if (shouldLoadBrokerOptions) {
        const shouldLoadEnvVarSets = isStartLikeAction(nextAction?.actionType);
        const [brokers, snapshots, envVarSets] = await Promise.all([
          listBrokerCredentials(token).catch(() => []),
          listBrokerSnapshots(token, { includeLatest: true }).catch(() => ({ items: [] })),
          shouldLoadEnvVarSets ? listEnvVarSets(token).catch(() => []) : Promise.resolve([]),
        ]);
        const snapshotsByCredentialId = new Map(
          (snapshots.items || []).map((snapshot) => [snapshot.brokerCredentialId, snapshot]),
        );
        const brokersWithSnapshots = brokers.map((broker) => ({
          ...broker,
          latestAccountValue: getSnapshotAccountValue(snapshotsByCredentialId.get(broker.id)),
        }));
        setBrokerOptions(brokersWithSnapshots);
        const requestedBrokerId = selectedBrokerIdFromAction(nextAction);
        const returnedBrokerId =
          new URLSearchParams(location.search).get('brokerCredentialId') || '';
        const activeBrokers = brokersWithSnapshots.filter((item) => !brokerNeedsReconnect(item));
        const selectableBrokers = activeBrokers;
        const knownReturnedBrokerId = selectableBrokers.some((item) => item.id === returnedBrokerId)
          ? returnedBrokerId
          : '';
        const knownRequestedBrokerId = selectableBrokers.some(
          (item) => item.id === requestedBrokerId,
        )
          ? requestedBrokerId
          : '';
        const automaticSingleBrokerId =
          !knownRequestedBrokerId && selectableBrokers.length === 1 ? selectableBrokers[0].id : '';
        setSelectedBrokerCredentialId((current) => {
          if (knownReturnedBrokerId) return knownReturnedBrokerId;
          if (current && selectableBrokers.some((item) => item.id === current)) return current;
          return knownRequestedBrokerId || automaticSingleBrokerId;
        });
        if (shouldLoadEnvVarSets) {
          setEnvVarOptions(envVarSets);
          const reservedEnvIds = new Set(
            envVarSets
              .filter((item) => hasReservedBrokerEnvVarName(item.keyNames))
              .map((item) => item.id),
          );
          const requestedEnvIds = selectedEnvIdsFromAction(nextAction).filter(
            (id) => !reservedEnvIds.has(id),
          );
          setSelectedEnvVarSetIds((current) => {
            const filteredCurrent = current.filter((id) => !reservedEnvIds.has(id));
            // Only the server or the owner selects saved secrets, never the browser's analysis guess.
            return filteredCurrent.length ? filteredCurrent : requestedEnvIds;
          });
        } else {
          setEnvVarOptions([]);
          setSelectedEnvVarSetIds([]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load authorization request.');
    } finally {
      setLoading(false);
    }
  }, [actionId, getApiToken, getFreshLoginIdToken, isAuthenticated, location.search]);

  useEffect(() => {
    setAcceptedStopNonLiquidation(false);
    setAcceptedLegacyHistoryAccountCorrection(false);
    setLegacyHistoryAccountCorrection(null);
  }, [actionId]);

  useEffect(() => {
    if (!isLoading) {
      loadAction();
    }
  }, [isLoading, loadAction]);

  const submitDecision = async (decision) => {
    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const token = await getApiToken();
      if (!token) {
        throw new Error('Authentication token was unavailable.');
      }
      const freshLoginIdToken =
        decision === 'approve' && typeof getFreshLoginIdToken === 'function'
          ? await getFreshLoginIdToken()
          : null;
      const approvalBody = freshLoginIdToken ? { freshLoginIdToken } : {};
      if (decision === 'approve' && isStartLikeAction(action?.actionType)) {
        if (!selectedBrokerCredentialId) {
          throw new Error('Choose a saved broker connection before continuing.');
        }
        if (selectedBrokerNeedsReconnect) {
          throw new Error('Reconnect this broker before continuing.');
        }
        approvalBody.brokerCredentialId = selectedBrokerCredentialId;
        approvalBody.envVarSetIds = selectedEnvVarSetIds;
      }
      if (decision === 'approve' && action?.actionType === 'place_trade') {
        if (!selectedBrokerCredentialId) {
          throw new Error('Choose a saved broker connection before continuing.');
        }
        if (selectedBrokerNeedsReconnect) {
          throw new Error('Reconnect this broker before continuing.');
        }
        approvalBody.brokerCredentialId = selectedBrokerCredentialId;
        approvalBody.trade = tradeDraft;
      }
      if (decision === 'approve' && shouldCollectLiveTerms && acceptedTerms && acceptedLiveRisk) {
        approvalBody.acceptances = {
          acceptedTerms: true,
          acceptedBetaRisk: true,
        };
      }
      if (
        decision === 'approve' &&
        legacyHistoryAccountCorrection &&
        acceptedLegacyHistoryAccountCorrection
      ) {
        approvalBody.acceptances = {
          ...(approvalBody.acceptances || {}),
          acceptedLegacyHistoryAccountCorrection: true,
        };
      }
      const response = await fetch(`${API_URL}/action-authorizations/${actionId}/${decision}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(approvalBody),
      });
      const data = await response.json().catch(() => ({}));
      if (
        isLegacyHistoryAccountCorrectionError(data) &&
        (!data.action || data.action.status === 'pending_authorization')
      ) {
        if (data.action) setAction(data.action);
        setAcceptedLegacyHistoryAccountCorrection(false);
        setLegacyHistoryAccountCorrection({
          message:
            String(data.message || '').trim() ||
            'This legacy history is attached to another saved broker account.',
          selectedExternalAccountId:
            String(data?.details?.selectedExternalAccountId || '').trim() ||
            getAuthoritativeBrokerAccountId(selectedBroker),
        });
        setError('');
        return;
      }
      if (data.action) {
        setAction(data.action);
      }
      if (!response.ok) {
        const needsFreshLogin =
          data?.code === 'fresh_login_required' || data?.errorCode === 'fresh_login_required';
        if (needsFreshLogin && data.action) {
          throw new Error(data.message || `Failed to ${decision} action.`);
        }
        const visibleError = customerFacingDecisionError(data.message, data.action);
        if (!visibleError) {
          setError('');
          return;
        }
        throw new Error(visibleError);
      }
      setResult(data);
      setError('');
    } catch (err) {
      setError(err.message || `Failed to ${decision} action.`);
    } finally {
      setSubmitting(false);
    }
  };

  const buildLoginPath = (freshLogin = false) => {
    const currentReturnTo = `${location.pathname || ''}${location.search || ''}`;
    const returnTo =
      currentReturnTo && location.pathname !== '/'
        ? currentReturnTo
        : `/authorize-action/${actionId}`;
    const params = new URLSearchParams({
      returnTo,
      source: 'action_authorization',
    });
    if (freshLogin) {
      params.set('freshLogin', '1');
      params.set('actionId', actionId);
    }
    return `/login?${params.toString()}`;
  };

  const requestLogin = () => {
    navigate(buildLoginPath(false));
  };

  const requestFreshLogin = () => {
    navigate(buildLoginPath(true));
  };

  const selectedBroker = useMemo(
    () => brokerOptions.find((item) => item.id === selectedBrokerCredentialId) || null,
    [brokerOptions, selectedBrokerCredentialId],
  );
  const actionForDetails = useMemo(() => {
    if (action?.actionType !== 'place_trade') return action;
    return {
      ...action,
      payloadRedacted: {
        ...(action?.payloadRedacted || {}),
        trade: tradeDraft,
      },
    };
  }, [action, tradeDraft]);
  const details = useMemo(
    () => getDeploymentDetails(actionForDetails, selectedBroker),
    [actionForDetails, selectedBroker],
  );
  const activeBrokerOptions = useMemo(
    () =>
      brokerOptions
        .filter((item) => !brokerNeedsReconnect(item))
        .slice()
        .sort((a, b) => brokerConnectionSortKey(a).localeCompare(brokerConnectionSortKey(b))),
    [brokerOptions],
  );
  const requestedBrokerCredentialId = useMemo(() => selectedBrokerIdFromAction(action), [action]);
  const requestedBrokerNeedsReconnect = useMemo(
    () =>
      Boolean(requestedBrokerCredentialId) &&
      brokerNeedsReconnect(brokerOptions.find((item) => item.id === requestedBrokerCredentialId)),
    [brokerOptions, requestedBrokerCredentialId],
  );
  const selectedBrokerNeedsReconnect = brokerNeedsReconnect(selectedBroker);
  const addBrokerPath = useMemo(() => {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search || ''}`);
    return `/account-settings/broker-connections?action=add&returnTo=${returnTo}`;
  }, [location.pathname, location.search]);
  const addEnvVarPath = useMemo(() => {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search || ''}`);
    return `/account-settings/saved-env-vars?action=add&returnTo=${returnTo}`;
  }, [location.pathname, location.search]);
  const deploymentId = useMemo(
    () =>
      action?.resultResourceId ||
      result?.action?.resultResourceId ||
      result?.resultResourceId ||
      result?.result?.deployId ||
      result?.result?.deploymentId ||
      null,
    [action, result],
  );
  const isPending = action?.status === 'pending_authorization';
  const needsFreshLogin = Boolean(action?.approvalRequirements?.freshLogin);
  const freshLoginSatisfied =
    !needsFreshLogin || action?.approvalRequirements?.freshLoginSatisfied === true;
  const needsBroker = isStartLikeAction(action?.actionType) || action?.actionType === 'place_trade';
  const isStopAction = action?.actionType === 'stop_deployment';
  const isTradeAction = action?.actionType === 'place_trade';
  const liveModeRequested =
    actionPaperOrLive(action) === 'live' || selectedBroker?.paperOrLive === 'live';
  const needsLiveTerms = Boolean(
    (isStartLikeAction(action?.actionType) || isTradeAction) &&
    (action?.metadata?.requiresLiveTradingAcceptance === true || liveModeRequested),
  );
  const shouldCollectLiveTerms = needsLiveTerms && freshLoginSatisfied;
  const liveTermsAccepted = !shouldCollectLiveTerms || (acceptedTerms && acceptedLiveRisk);
  const shouldCollectLegacyHistoryAccountCorrection =
    isPending &&
    isStartLikeAction(action?.actionType) &&
    freshLoginSatisfied &&
    Boolean(legacyHistoryAccountCorrection);
  const legacyHistoryAccountCorrectionAccepted =
    !shouldCollectLegacyHistoryAccountCorrection || acceptedLegacyHistoryAccountCorrection;
  const shouldCollectStopAcknowledgement = isPending && isStopAction && freshLoginSatisfied;
  const stopAcknowledgementAccepted =
    !shouldCollectStopAcknowledgement || acceptedStopNonLiquidation;
  const selectedEnvVarSets = useMemo(
    () => envVarOptions.filter((item) => selectedEnvVarSetIds.includes(item.id)),
    [envVarOptions, selectedEnvVarSetIds],
  );
  const runtimeRequirements = useMemo(() => normalizeRuntimeRequirements(action), [action]);
  const selectedEnvKeys = useMemo(
    () => collectSelectedEnvKeys(selectedEnvVarSets),
    [selectedEnvVarSets],
  );
  const missingRuntimeRequirements = useMemo(
    () =>
      runtimeRequirements.filter(
        (requirement) => !runtimeRequirementSatisfied(requirement, selectedEnvKeys),
      ),
    [runtimeRequirements, selectedEnvKeys],
  );
  const runtimeRequirementsSatisfied = missingRuntimeRequirements.length === 0;
  const runtimePlatformBlockers = action?.runtimeSecretReadiness?.blockers ?? [];
  const requiredEnvKeys = useMemo(() => {
    const keys = new Set();
    runtimeRequirements.forEach((requirement) => {
      requirement.allOf.forEach((item) => keys.add(item.key));
      requirement.anyOf.forEach((item) => keys.add(item.key));
    });
    return keys;
  }, [runtimeRequirements]);
  const canApprove =
    isPending &&
    !submitting &&
    freshLoginSatisfied &&
    (!needsBroker || (Boolean(selectedBrokerCredentialId) && !selectedBrokerNeedsReconnect)) &&
    runtimeRequirementsSatisfied &&
    runtimePlatformBlockers.length === 0 &&
    liveTermsAccepted &&
    legacyHistoryAccountCorrectionAccepted &&
    stopAcknowledgementAccepted;
  const canConfirmLogin = isPending && !submitting && needsFreshLogin && !freshLoginSatisfied;
  const ownsCompletedDeploymentNavigation = !actionIdOverride;
  const terminalCopy = statusCopy(action);
  const pageTitle = isPending ? actionTitle(action?.actionType) : completedActionTitle(action);
  const approveLabel = actionLabel(action?.actionType);
  const loginLabel = confirmLoginLabel(action?.actionType);
  const schedulePreview = useMemo(() => getActionSchedulePreview(action), [action]);
  const scheduleLabel = formatScheduleRouteLabel(schedulePreview);
  const scheduleCadenceLabel = formatScheduleCadenceLabel(schedulePreview);
  const filteredEnvVarOptions = useMemo(
    () =>
      envVarOptions
        .filter(
          (item) =>
            !envVarSearch.trim() ||
            formatEnvVarSearchText(item).includes(envVarSearch.trim().toLowerCase()),
        )
        .sort((left, right) => {
          const leftMatches = envVarRequiredKeyMatchCount(left, requiredEnvKeys);
          const rightMatches = envVarRequiredKeyMatchCount(right, requiredEnvKeys);
          if (leftMatches !== rightMatches) return rightMatches - leftMatches;
          return formatEnvVarSet(left).localeCompare(formatEnvVarSet(right));
        }),
    [envVarOptions, envVarSearch, requiredEnvKeys],
  );
  const toggleEnvVarSet = (id) => {
    const item = envVarOptions.find((entry) => entry.id === id);
    if (hasReservedBrokerEnvVarName(item?.keyNames)) return;
    setSelectedEnvVarSetIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  useEffect(() => {
    if (
      ownsCompletedDeploymentNavigation &&
      action?.status === 'completed' &&
      isStartLikeAction(action?.actionType) &&
      deploymentId
    ) {
      const timer = setTimeout(() => {
        navigate(`/deployments/${encodeURIComponent(deploymentId)}`);
      }, 3500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    action?.status,
    action?.actionType,
    deploymentId,
    navigate,
    ownsCompletedDeploymentNavigation,
  ]);

  if (loading || isLoading) {
    return (
      <section className={pageClassName}>
        <div className="action-auth-shell">
          <div className="action-auth-panel">
            <p className="action-auth-muted">Loading authorization request...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!isAuthenticated) {
    return (
      <section className={pageClassName}>
        <div className="action-auth-shell">
          <div className="action-auth-panel">
            <div className="action-auth-hero">
              <h1>Sign in to review this request</h1>
              <p>Use your BotSpot login to review and authorize this action.</p>
            </div>
            <div className="action-auth-actions">
              <button className="action-auth-primary" type="button" onClick={requestLogin}>
                Sign in to BotSpot
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={pageClassName}>
      <div className="action-auth-shell">
        <div className="action-auth-panel">
          {error && isPending && <div className="action-auth-error">{error}</div>}

          {action ? (
            <ActionAuthorizationCard
              actionType={action.actionType}
              title={pageTitle}
              subtitle={null}
              statusMessage={!isPending ? terminalCopy : ''}
              statusTone={!isPending ? actionStatusTone(action) : ''}
              isPending={isPending}
              submitting={submitting}
              canApprove={canApprove}
              canConfirmLogin={canConfirmLogin}
              confirmLoginLabel={loginLabel}
              approveLabel={approveLabel}
              approveIcon={actionCtaIcon(action?.actionType)}
              onApprove={() => submitDecision('approve')}
              onConfirmLogin={requestFreshLogin}
              onDeny={isTradeAction ? undefined : () => submitDecision('deny')}
              tradeDetails={isTradeAction ? details : null}
              brokerOptions={activeBrokerOptions}
              selectedBrokerCredentialId={selectedBrokerCredentialId}
              onBrokerChange={setSelectedBrokerCredentialId}
              addBrokerPath={addBrokerPath}
              tradeDraft={tradeDraft}
              onTradeChange={(field, value) => {
                setTradeDraft((current) => {
                  const next = { ...current, [field]: value };
                  if (field === 'orderType') {
                    if (value === 'market') {
                      delete next.limitPrice;
                      delete next.stopPrice;
                      delete next.stopLimitPrice;
                    } else if (value === 'limit') {
                      delete next.stopPrice;
                      delete next.stopLimitPrice;
                    } else if (value === 'stop') {
                      delete next.limitPrice;
                      delete next.stopLimitPrice;
                    }
                  }
                  return next;
                });
              }}
            >
              {!isTradeAction && (
                <div className="action-auth-request-wide action-auth-summary-row action-auth-name-row">
                  <span>Name</span>
                  <strong>{details.strategyName}</strong>
                </div>
              )}
              {action.actionType === 'update_deployment_revision' && details.versionChanged && (
                <div
                  className="action-auth-request-wide action-auth-sequence"
                  aria-label="Update sequence"
                >
                  <span>
                    <strong>{details.currentVersion}</strong>
                    <small>Current bot version</small>
                  </span>
                  <span aria-hidden="true">to</span>
                  <span>
                    <strong>{details.targetVersion}</strong>
                    <small>Version to start after stop</small>
                  </span>
                </div>
              )}
              {action.actionType === 'update_deployment_revision' && !details.versionChanged && (
                <div className="action-auth-request-wide action-auth-summary-row">
                  <span>Version</span>
                  <strong>{details.targetVersion}</strong>
                </div>
              )}
              {isStartLikeAction(action.actionType) &&
                schedulePreview &&
                (scheduleLabel || scheduleCadenceLabel) && (
                  <div
                    className="action-auth-request-wide action-auth-schedule-summary"
                    aria-label="Schedule summary"
                  >
                    {scheduleLabel && (
                      <div className="action-auth-schedule-row">
                        <span>Type</span>
                        <strong>{scheduleLabel}</strong>
                      </div>
                    )}
                    {scheduleCadenceLabel && (
                      <div className="action-auth-schedule-row">
                        <span>Cadence</span>
                        <strong>{scheduleCadenceLabel}</strong>
                      </div>
                    )}
                  </div>
                )}
              {isStartLikeAction(action.actionType) && (
                <>
                  <div className="action-auth-request-wide action-auth-field">
                    <div className="action-auth-field-head">
                      <label htmlFor="brokerCredentialId">Broker account</label>
                      {isPending && (
                        <Link className="action-auth-inline-link" to={addBrokerPath}>
                          Add broker
                        </Link>
                      )}
                    </div>
                    <select
                      id="brokerCredentialId"
                      value={selectedBrokerCredentialId}
                      onChange={(event) => {
                        setSelectedBrokerCredentialId(event.target.value);
                        setAcceptedLegacyHistoryAccountCorrection(false);
                        setLegacyHistoryAccountCorrection(null);
                      }}
                      disabled={!isPending || submitting}
                    >
                      <option value="">Choose a saved broker connection</option>
                      {activeBrokerOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {formatBrokerConnection(item)}
                        </option>
                      ))}
                    </select>
                    {brokerOptions.length === 0 && (
                      <p className="action-auth-field-help">
                        No saved broker connections yet. Add one, then come back to approve this
                        bot.
                      </p>
                    )}
                    {requestedBrokerNeedsReconnect && (
                      <p className="action-auth-field-help action-auth-field-warning">
                        The requested broker needs reconnect. Choose another broker or reconnect it
                        first.
                      </p>
                    )}
                    {!requestedBrokerNeedsReconnect && selectedBrokerNeedsReconnect && (
                      <p className="action-auth-field-help action-auth-field-warning">
                        Reconnect this broker before starting this bot.
                      </p>
                    )}
                    {selectedBroker && (
                      <details className="action-auth-broker-disclosure">
                        <summary>Account details</summary>
                        <div
                          className="action-auth-selected-broker"
                          aria-label="Selected broker account details"
                        >
                          <div>
                            <span>External account ID</span>
                            <strong>{getAuthoritativeBrokerAccountId(selectedBroker)}</strong>
                          </div>
                          <div>
                            <span>Latest account value</span>
                            <strong>{formatAccountValue(selectedBroker.latestAccountValue)}</strong>
                          </div>
                          {getBrokerConnectionTitle(selectedBroker) && (
                            <small>{getBrokerConnectionTitle(selectedBroker)}</small>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                  <div className="action-auth-request-wide action-auth-env">
                    {action?.runtimeSecretReadiness?.managedCredentialKeys?.length > 0 && (
                      <p className="action-auth-muted">
                        AI billing: <strong>BotSpot AI credits</strong>
                      </p>
                    )}
                    {runtimePlatformBlockers.length > 0 && (
                      <div className="action-auth-required-env needs-selection" role="alert">
                        <strong>
                          {runtimePlatformBlockers.map((blocker) => blocker.message).join(' ')}
                        </strong>
                      </div>
                    )}
                    {runtimePlatformBlockers.length === 0 &&
                      runtimeRequirements.length > 0 &&
                      !runtimeRequirementsSatisfied && (
                        <div className="action-auth-required-env needs-selection" role="alert">
                          <div className="action-auth-required-env-head">
                            <strong>
                              Add or select{' '}
                              {missingRuntimeRequirements.map(requirementDisplayKeys).join('; ')} to
                              continue.
                            </strong>
                            <Link className="action-auth-inline-link" to={addEnvVarPath}>
                              Add saved variable
                            </Link>
                          </div>
                        </div>
                      )}
                    <button
                      aria-label="Saved variables"
                      aria-expanded={envVarsExpanded}
                      className="action-auth-env-toggle"
                      type="button"
                      onClick={() => setEnvVarsExpanded((current) => !current)}
                    >
                      <span>
                        <span className="action-auth-env-label">
                          {runtimeRequirements.length || selectedEnvVarSets.length
                            ? 'Saved variables'
                            : 'Additional settings'}
                        </span>
                        {selectedEnvVarSets.length > 0 && (
                          <strong>{selectedEnvVarSets.length} selected</strong>
                        )}
                      </span>
                      <span>{envVarsExpanded ? 'Hide' : 'Review'}</span>
                    </button>
                    {envVarsExpanded && (
                      <div className="action-auth-env-panel">
                        {runtimeRequirementsSatisfied && (
                          <Link className="action-auth-inline-link" to={addEnvVarPath}>
                            Add saved variable
                          </Link>
                        )}
                        <input
                          aria-label="Search saved variables"
                          className="action-auth-env-search"
                          placeholder="Search saved variables"
                          type="search"
                          value={envVarSearch}
                          onChange={(event) => setEnvVarSearch(event.target.value)}
                        />
                        {filteredEnvVarOptions.length ? (
                          <div className="action-auth-checklist">
                            {filteredEnvVarOptions.map((item) => {
                              const reservedNames = getReservedBrokerEnvVarNames(item.keyNames);
                              const isReserved = reservedNames.length > 0;
                              const requiredMatchCount = envVarRequiredKeyMatchCount(
                                item,
                                requiredEnvKeys,
                              );
                              const isSelected = selectedEnvVarSetIds.includes(item.id);
                              const summary = formatEnvVarSetSummary(item, requiredMatchCount);
                              return (
                                <label
                                  key={item.id}
                                  className={[
                                    'action-auth-check',
                                    isSelected ? 'is-selected' : '',
                                    isReserved ? 'is-disabled' : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={!isPending || submitting || isReserved}
                                    onChange={() => toggleEnvVarSet(item.id)}
                                  />
                                  <span>
                                    <strong>
                                      {formatEnvVarSet(item)}
                                      {requiredMatchCount > 0 && (
                                        <em className="action-auth-env-match">
                                          Matches required keys
                                        </em>
                                      )}
                                    </strong>
                                    {isReserved ? (
                                      <small>
                                        Reserved for broker connections: {reservedNames.join(', ')}
                                      </small>
                                    ) : (
                                      summary && <small>{summary}</small>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="action-auth-muted">No saved variables match that search.</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
              {shouldCollectLiveTerms && (
                <div
                  className="action-auth-request-wide action-auth-live-terms"
                  aria-label="Live trading terms"
                >
                  <label className="action-auth-check action-auth-live-term">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      disabled={!isPending || submitting}
                      onChange={(event) => setAcceptedTerms(event.target.checked)}
                    />
                    <span>
                      <strong>
                        I accept the{' '}
                        <Link to="/terms" target="_blank" rel="noopener noreferrer">
                          Terms of Service
                        </Link>
                      </strong>
                    </span>
                  </label>
                  <label className="action-auth-check action-auth-live-term">
                    <input
                      type="checkbox"
                      checked={acceptedLiveRisk}
                      disabled={!isPending || submitting}
                      onChange={(event) => setAcceptedLiveRisk(event.target.checked)}
                    />
                    <span>
                      <strong>{liveRiskAcknowledgementText(action.actionType)}</strong>
                      <small>
                        Losses are permanent. Automated trading software, including AI-generated
                        strategies, may produce missed, duplicate, or unintended orders due to bugs,
                        outages, or connectivity failures. AI-generated strategies are not
                        investment advice. I am solely responsible for monitoring my positions,
                        account balances, and all open orders at all times.
                      </small>
                    </span>
                  </label>
                </div>
              )}
              {shouldCollectLegacyHistoryAccountCorrection && (
                <div
                  className="action-auth-request-wide action-auth-account-correction"
                  aria-label="Legacy history account correction"
                >
                  <p>{legacyHistoryAccountCorrection.message}</p>
                  <label className="action-auth-check action-auth-account-correction-term">
                    <input
                      type="checkbox"
                      checked={acceptedLegacyHistoryAccountCorrection}
                      disabled={!isPending || submitting}
                      onChange={(event) =>
                        setAcceptedLegacyHistoryAccountCorrection(event.target.checked)
                      }
                    />
                    <span>
                      <strong>
                        I confirm {legacyHistoryAccountCorrection.selectedExternalAccountId} is the
                        broker account that owns this strategy&apos;s previous history.
                      </strong>
                    </span>
                  </label>
                </div>
              )}
              {shouldCollectStopAcknowledgement && (
                <div
                  className="action-auth-request-wide action-auth-stop-acknowledgement"
                  aria-label="Stop bot position acknowledgement"
                >
                  <label className="action-auth-check action-auth-stop-term">
                    <input
                      type="checkbox"
                      checked={acceptedStopNonLiquidation}
                      disabled={!isPending || submitting}
                      onChange={(event) => setAcceptedStopNonLiquidation(event.target.checked)}
                    />
                    <span>
                      <strong>
                        I understand stopping this bot will not close existing positions.
                      </strong>
                      <small>{STOP_BOT_POSITION_ACKNOWLEDGEMENT}</small>
                    </span>
                  </label>
                </div>
              )}
              {isPending ? (
                <>
                  {!selectedBrokerCredentialId && isStartLikeAction(action.actionType) && (
                    <div className="action-auth-security action-auth-security-warning">
                      Choose a saved broker connection before continuing.
                    </div>
                  )}
                  {selectedBrokerNeedsReconnect && isStartLikeAction(action.actionType) && (
                    <div className="action-auth-security action-auth-security-warning">
                      Reconnect this broker before continuing.
                    </div>
                  )}
                  {shouldCollectLiveTerms && !liveTermsAccepted && (
                    <div className="action-auth-security action-auth-security-warning">
                      Accept the terms and live trading risk note before continuing.
                    </div>
                  )}
                  {shouldCollectLegacyHistoryAccountCorrection &&
                    !legacyHistoryAccountCorrectionAccepted && (
                      <div className="action-auth-security action-auth-security-warning">
                        Confirm the authoritative broker account before moving the previous history.
                      </div>
                    )}
                  {shouldCollectStopAcknowledgement && !stopAcknowledgementAccepted && (
                    <div className="action-auth-security action-auth-security-warning">
                      Acknowledge that stopping this bot will not close existing broker positions
                      before continuing.
                    </div>
                  )}
                </>
              ) : null}
              {!isPending && isStartLikeAction(action.actionType) && deploymentId && (
                <div className="action-auth-request-wide action-auth-next-step">
                  <Link
                    className="action-auth-primary"
                    to={`/deployments/${encodeURIComponent(deploymentId)}`}
                  >
                    <FontAwesomeIcon
                      icon={faEye}
                      className="action-auth-primary-icon"
                      aria-hidden="true"
                    />
                    <span>Watch Bot</span>
                  </Link>
                  {ownsCompletedDeploymentNavigation ? (
                    <span>Opening the bot page automatically.</span>
                  ) : null}
                </div>
              )}
              {isPending && (
                <p className="action-auth-request-wide action-auth-footnote">
                  Expires {details.expiresAt}.
                </p>
              )}
            </ActionAuthorizationCard>
          ) : (
            <p className="action-auth-muted">Authorization request not found.</p>
          )}
        </div>
      </div>
    </section>
  );
}
